/* ===== game/boss-ice-art.ts — 얼음 감시자 · 서리 기술의 얼음 그림(결정 기둥 · 빙하 판 · 서리 광선) ===== */
import { TAU } from '../../engine/core/math.js';
/* ★ 흰 막대 · 고른 울타리로 그리지 않는다(점검판 2026-11-03 "얼음 감옥과 방벽이 이질감이 크다 — 사실적으로").
   얼음은 속이 비치는 덩어리다: 밑동은 짙은 청록 · 끝은 거의 흰빛, 면마다 밝기가 다르고 속에 금 · 기포가 있으며 윤곽선은 옅다.
   크기 · 기울기 · 개수는 칸마다 해시로 흩는다 — 같은 모양이 줄지어 서면 다시 '이질감'이 된다. */
/* 사연: docs/code-history.md#h261 */

type C2 = CanvasRenderingContext2D;
const hs = (s: number, i: number) => { const v = Math.sin(s * 127.1 + i * 311.7) * 43758.5453; return v - Math.floor(v); };

/** 얼음 결정 기둥 — 육각 기둥을 옆에서 본 세 면 + 비스듬히 깎인 끝. lean 은 끝이 기우는 정도(px) */
export function iceSpire(c: C2, sd: number, x: number, y: number, w: number, hgt: number, lean = 0) {
  if (hgt < 2) return;
  const hw = w / 2, tipH = Math.min(hgt * 0.45, w * (0.9 + hs(sd, 1) * 0.6)), sh = hgt - tipH;
  const tx = x + lean, ty = y - hgt, mx = x + lean * (sh / hgt), my = y - sh;   // 끝 · 어깨
  const off = (hs(sd, 2) - 0.5) * hw * 0.5;                              // 끝이 가운데에서 비켜 깎였다
  const L = [x - hw, y, mx - hw, my, tx + off, ty, x - hw * 0.25 + lean * 0.5, my + tipH * 0.25, x - hw * 0.25, y];
  const M = [x - hw * 0.25, y, x - hw * 0.25 + lean * 0.5, my + tipH * 0.25, tx + off, ty, mx + hw * 0.45, my - tipH * 0.1, x + hw * 0.45, y];
  const R = [x + hw * 0.45, y, mx + hw * 0.45, my - tipH * 0.1, tx + off, ty, mx + hw, my, x + hw, y];
  const face = (p: number[], a: string, b: string) => {
    c.beginPath(); c.moveTo(p[0], p[1]); for (let i = 2; i < p.length; i += 2) c.lineTo(p[i], p[i + 1]); c.closePath();
    const g = c.createLinearGradient(0, y, 0, ty); g.addColorStop(0, a); g.addColorStop(1, b); c.fillStyle = g; c.fill();
  };
  face(L, 'rgba(34,96,150,.92)', 'rgba(150,215,245,.9)');               // 그늘 면
  face(M, 'rgba(70,150,205,.88)', 'rgba(235,252,255,.95)');             // 빛 받는 면
  face(R, 'rgba(48,120,178,.9)', 'rgba(190,235,252,.92)');
  c.save(); c.beginPath(); c.moveTo(L[0], L[1]); c.lineTo(L[2], L[3]); c.lineTo(tx + off, ty); c.lineTo(R[6], R[7]); c.lineTo(R[8], R[9]); c.closePath(); c.clip();
  c.strokeStyle = 'rgba(255,255,255,.55)'; c.lineWidth = 1;              // 속 금 — 흰 줄과 그 밑 그늘
  for (let i = 0; i < 2; i++) {
    const cy = y - hgt * (0.25 + hs(sd, i + 5) * 0.45), cx = x + (hs(sd, i + 7) - 0.5) * w * 0.6;
    c.beginPath(); c.moveTo(cx - w * 0.3, cy + 4); c.lineTo(cx, cy); c.lineTo(cx + w * 0.25, cy - 6 - hs(sd, i) * 6); c.stroke();
  }
  c.fillStyle = 'rgba(230,250,255,.6)';                                  // 갇힌 기포
  for (let i = 0; i < 4; i++) { c.beginPath(); c.arc(x + (hs(sd, i + 11) - 0.5) * w * 0.7, y - hgt * hs(sd, i + 13) * 0.7, 0.8 + hs(sd, i + 17) * 1.2, 0, TAU); c.fill(); }
  const g = c.createLinearGradient(0, y, 0, y - Math.min(hgt, 30)); g.addColorStop(0, 'rgba(16,50,90,.45)'); g.addColorStop(1, 'rgba(16,50,90,0)');
  c.fillStyle = g; c.fillRect(x - hw - 2, y - 30, w + 4, 30);           // 밑동은 짙다(땅에 박힌 두께)
  c.restore();
  c.strokeStyle = 'rgba(255,255,255,.9)'; c.lineWidth = 1.2;            // 모서리 빛 — 빛 받는 면의 왼쪽 날
  c.beginPath(); c.moveTo(M[0], M[1] - 2); c.lineTo(M[2], M[3]); c.lineTo(tx + off, ty); c.stroke();
  c.strokeStyle = 'rgba(20,60,105,.55)'; c.lineWidth = 1;                // 옅은 윤곽
  c.beginPath(); c.moveTo(L[0], L[1]); c.lineTo(L[2], L[3]); c.lineTo(tx + off, ty); c.lineTo(R[6], R[7]); c.lineTo(R[8], R[9]); c.stroke();
}

/** 눈 더미 — 얼음이 솟은 자리의 밑동(바닥 이음매를 가린다) */
export function snowDrift(c: C2, sd: number, x: number, y: number, rx: number, ry: number) {
  c.beginPath(); c.moveTo(x - rx, y);
  for (let i = 0; i <= 8; i++) { const u = i / 8, a = Math.PI * (1 - u); c.lineTo(x + Math.cos(a) * rx, y - Math.sin(a) * ry * (0.75 + hs(sd, i) * 0.4)); }
  c.closePath();
  const g = c.createLinearGradient(0, y - ry, 0, y); g.addColorStop(0, '#f6fcff'); g.addColorStop(1, '#a9cfe6');
  c.fillStyle = g; c.fill(); c.strokeStyle = 'rgba(70,110,150,.45)'; c.lineWidth = 1; c.stroke();
}

/** 결정 무리 — 감옥 기둥 한 칸. 가운데가 가장 높고 바깥 결정은 바깥으로 기운다. grow 0~1 = 솟는 중 */
export function iceGrove(c: C2, sd: number, x: number, y: number, w: number, hgt: number, grow: number) {
  const n = 3 + Math.floor(hs(sd, 21) * 3), e = grow < 1 ? 1 - (1 - grow) ** 3 * (1 + 2.2 * grow) : 1;   // 살짝 넘쳤다 앉는다
  const order: number[] = []; for (let i = 0; i < n; i++) order.push(i);
  order.sort((a, b) => Math.abs(b - (n - 1) / 2) - Math.abs(a - (n - 1) / 2));   // 바깥 것부터 — 가운데 큰 결정이 앞에
  for (const i of order) {
    const u = n > 1 ? i / (n - 1) - 0.5 : 0, s2 = sd * 3.1 + i * 7.7;
    const hh = hgt * e * (1 - Math.abs(u) * 0.9) * (0.75 + hs(s2, 1) * 0.35);
    const ww = w * (0.36 - Math.abs(u) * 0.12) * (0.85 + hs(s2, 2) * 0.3);
    iceSpire(c, s2, x + u * w * 0.85, y, ww, hh, u * hh * 0.35 + (hs(s2, 3) - 0.5) * 6);
  }
  snowDrift(c, sd, x, y + 1, w * 0.62, 5 + hs(sd, 23) * 5);
}

/** 빙하 판 — 다가오는 빙벽. 위가 땅에 닿지 않은 높은 벽은 밑 모서리에 고드름 */
export function glacierSlab(c: C2, sd: number, x: number, top: number, bot: number, w: number, dir: number, grounded: boolean, T: number) {
  const hgt = bot - top, n = Math.max(3, Math.round(hgt / 26)), hw = w / 2;
  const lp: number[] = [], rp: number[] = [];                           // 좌우 날 — 덩어리가 쌓인 듯 들쭉날쭉
  for (let i = 0; i <= n; i++) {
    const yy = top + hgt * i / n;
    lp.push(x - hw - (hs(sd, i) - 0.3) * 8 * (i && i < n ? 1 : 0.3), yy);
    rp.push(x + hw + (hs(sd, i + 40) - 0.3) * 8 * (i && i < n ? 1 : 0.3), yy);
  }
  const outline = () => {
    c.beginPath(); c.moveTo(lp[0], lp[1]);
    for (let i = 0; i <= 5; i++) c.lineTo(x - hw + w * i / 5, top - 2 - hs(sd, i + 60) * 9);   // 깎인 윗날
    for (let i = 0; i < rp.length; i += 2) c.lineTo(rp[i], rp[i + 1]);
    if (!grounded) for (let i = 6; i >= 0; i--) c.lineTo(x - hw + w * i / 6, bot + (i % 2 ? 4 + hs(sd, i + 80) * 6 : 0));
    for (let i = lp.length - 2; i >= 0; i -= 2) c.lineTo(lp[i], lp[i + 1]);
    c.closePath();
  };
  outline();
  const g = c.createLinearGradient(x - hw * dir, 0, x + hw * dir, 0);    // 앞날(가는 쪽)이 빛을 모은다
  g.addColorStop(0, 'rgba(30,86,140,.9)'); g.addColorStop(0.55, 'rgba(90,165,215,.88)'); g.addColorStop(0.85, 'rgba(210,244,255,.94)'); g.addColorStop(1, 'rgba(140,205,240,.9)');
  c.fillStyle = g; c.fill();
  c.save(); outline(); c.clip();
  const v = c.createLinearGradient(0, top, 0, bot); v.addColorStop(0, 'rgba(255,255,255,.35)'); v.addColorStop(0.3, 'rgba(255,255,255,0)'); v.addColorStop(1, 'rgba(10,40,80,.35)');
  c.fillStyle = v; c.fillRect(x - w, top - 12, w * 2, hgt + 24);
  c.strokeStyle = 'rgba(255,255,255,.5)'; c.lineWidth = 1;               // 켜 — 쌓인 얼음 층과 금
  for (let i = 1; i < n; i++) { const yy = top + hgt * i / n + (hs(sd, i + 90) - 0.5) * 6; c.beginPath(); c.moveTo(x - hw - 4, yy); c.lineTo(x - hw * 0.1, yy + (hs(sd, i + 95) - 0.5) * 8); c.lineTo(x + hw + 4, yy + (hs(sd, i + 99) - 0.5) * 6); c.stroke(); }
  c.strokeStyle = 'rgba(10,50,95,.4)';
  for (let i = 0; i < 2; i++) { const yy = top + hgt * (0.3 + 0.4 * hs(sd, i + 120)); c.beginPath(); c.moveTo(x - hw * 0.6, yy); c.lineTo(x + hw * 0.1, yy - 10); c.lineTo(x + hw * 0.4, yy - 4); c.stroke(); }
  c.fillStyle = 'rgba(235,252,255,.55)';
  for (let i = 0; i < 6; i++) { c.beginPath(); c.arc(x + (hs(sd, i + 130) - 0.5) * w * 0.8, top + hgt * hs(sd, i + 140), 0.8 + hs(sd, i + 150) * 1.4, 0, TAU); c.fill(); }
  const sw = ((T * 0.8 + hs(sd, 7)) % 1.6) / 1.6;                        // 표면을 훑는 반짝임
  if (sw < 1) { const yy = top + hgt * sw; const s = c.createLinearGradient(0, yy - 14, 0, yy + 14); s.addColorStop(0, 'rgba(255,255,255,0)'); s.addColorStop(0.5, 'rgba(255,255,255,.4)'); s.addColorStop(1, 'rgba(255,255,255,0)'); c.fillStyle = s; c.fillRect(x - w, yy - 14, w * 2, 28); }
  c.restore();
  c.strokeStyle = 'rgba(255,255,255,.85)'; c.lineWidth = 1.4;            // 앞날 빛
  c.beginPath(); const fx = dir > 0 ? rp : lp; c.moveTo(fx[0], fx[1]); for (let i = 2; i < fx.length; i += 2) c.lineTo(fx[i], fx[i + 1]); c.stroke();
  c.strokeStyle = 'rgba(20,60,105,.5)'; c.lineWidth = 1; outline(); c.stroke();
  if (grounded) {                                                        // 윗면 눈 · 밑동 눈보라
    c.fillStyle = '#f4fbff'; c.beginPath(); c.moveTo(x - hw - 2, top + 3);
    for (let i = 0; i <= 5; i++) c.lineTo(x - hw + w * i / 5, top - 3 - hs(sd, i + 60) * 9 + (i % 2 ? 2 : -1));
    c.lineTo(x + hw + 2, top + 4); c.quadraticCurveTo(x, top + 8, x - hw - 2, top + 3); c.fill();
  } else {                                                               // 높은 벽 — 밑 모서리에 매달린 고드름
    for (let i = 0; i < 4; i++) {
      const ix = x - hw * 0.8 + w * 0.8 * i / 3 + (hs(sd, i + 170) - 0.5) * 4, len = 8 + hs(sd, i + 175) * 16, iw = 4 + hs(sd, i + 179) * 3;
      c.beginPath(); c.moveTo(ix - iw / 2, bot - 1); c.lineTo(ix + iw / 2, bot - 1); c.lineTo(ix + 0.5, bot + len); c.closePath();
      const ig = c.createLinearGradient(0, bot, 0, bot + len); ig.addColorStop(0, 'rgba(120,195,235,.9)'); ig.addColorStop(1, 'rgba(240,252,255,.95)');
      c.fillStyle = ig; c.fill(); c.strokeStyle = 'rgba(20,60,105,.5)'; c.stroke();
    }
  }
}

/** 서리 광선 — 곧은 막대가 아니라 일렁이는 냉기 줄기: 흩어지는 김 · 흔들리는 심 · 줄기를 따라 맺혀 자라는 얼음 바늘 · 끝의 서리 분사.
    ★ 회전된 좌표(광선 = +x)에서 그린다. age = 쏜 뒤 지난 초 */
export function frostBeam(c: C2, sd: number, len: number, w: number, fade: number, age: number, T: number) {
  const op = c.globalCompositeOperation;
  const jit = (s: number) => Math.sin(s * 0.045 - T * 16) * w * 0.14 + Math.sin(s * 0.11 + T * 23) * w * 0.07;
  c.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 26; i++) {                                         // 김 — 줄기를 따라 흘러가며 부푼다
    const s = (hs(sd, i) * len + T * 420) % len, u = s / len, r = w * (0.7 + u * 0.9 + hs(sd, i + 30) * 0.5);
    const yy = jit(s) + (hs(sd, i + 60) - 0.5) * w * 0.9 * (0.4 + u);
    const g = c.createRadialGradient(s, yy, 0, s, yy, r);
    g.addColorStop(0, `rgba(170,225,255,${0.22 * fade})`); g.addColorStop(1, 'rgba(170,225,255,0)');
    c.fillStyle = g; c.beginPath(); c.arc(s, yy, r, 0, TAU); c.fill();
  }
  const path = () => { c.beginPath(); c.moveTo(0, 0); for (let s = 12; s <= len; s += 12) c.lineTo(s, jit(s) * Math.min(1, s / 60)); };
  c.lineCap = 'round'; c.lineJoin = 'round';
  path(); c.strokeStyle = `rgba(90,170,240,${0.35 * fade})`; c.lineWidth = w * 1.5; c.stroke();
  path(); c.strokeStyle = `rgba(170,230,255,${0.7 * fade})`; c.lineWidth = w * 0.7; c.stroke();
  path(); c.strokeStyle = `rgba(255,255,255,${0.95 * fade})`; c.lineWidth = Math.max(2, w * 0.2); c.stroke();
  for (let k = 0; k < 2; k++) {                                          // 심을 감아 도는 가는 냉기 가닥
    c.beginPath(); c.moveTo(0, 0);
    for (let s = 10; s <= len; s += 10) c.lineTo(s, jit(s) + Math.sin(s * 0.06 - T * 30 + k * Math.PI) * w * 0.38 * Math.min(1, s / 80));
    c.strokeStyle = `rgba(220,248,255,${0.5 * fade})`; c.lineWidth = 1.2; c.stroke();
  }
  c.globalCompositeOperation = op;
  const grow = Math.min(1, age * 1.6);                                   // 줄기 가장자리에 맺혀 자라는 얼음 바늘
  for (let s = 40, i = 0; s < len - 20; s += 30 + hs(sd, i) * 26, i++) {
    const side = hs(sd, i + 200) < 0.5 ? -1 : 1, l = (8 + hs(sd, i + 210) * 16) * grow * fade, base = jit(s) + side * w * 0.42;
    const tilt = 0.2 + hs(sd, i + 220) * 0.6;                            // 줄기 흐름 쪽으로 눕는다
    c.save(); c.translate(s, base); c.rotate(side > 0 ? -tilt : Math.PI + tilt);
    c.beginPath(); c.moveTo(-2.4, 0); c.lineTo(2.4, 0); c.lineTo(0.4, l); c.lineTo(-0.4, l * 0.9); c.closePath();
    const ig = c.createLinearGradient(0, 0, 0, l); ig.addColorStop(0, `rgba(120,195,240,${0.95 * fade})`); ig.addColorStop(1, `rgba(250,255,255,${fade})`);
    c.fillStyle = ig; c.fill(); c.strokeStyle = `rgba(30,80,130,${0.5 * fade})`; c.lineWidth = 0.8; c.stroke();
    c.restore();
  }
  c.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 12; i++) {                                         // 반짝이는 서리 알갱이(네 갈래 빛)
    const s = (hs(sd, i + 300) * len + T * 260 * (0.5 + hs(sd, i + 310))) % len, yy = jit(s) + (hs(sd, i + 320) - 0.5) * w * 1.6;
    const tw = 0.5 + 0.5 * Math.sin(T * 20 + i * 2.1), r = (2 + hs(sd, i + 330) * 4) * tw;
    c.strokeStyle = `rgba(255,255,255,${0.9 * fade * tw})`; c.lineWidth = 1;
    c.beginPath(); c.moveTo(s - r, yy); c.lineTo(s + r, yy); c.moveTo(s, yy - r); c.lineTo(s, yy + r); c.stroke();
  }
  for (let i = 0; i < 14; i++) {                                         // 끝 — 부딪혀 퍼지는 서리 분사
    const u = (T * 2.2 + hs(sd, i + 400)) % 1, a = (hs(sd, i + 410) - 0.5) * 1.8, d = u * w * 3.2;
    const g = c.createRadialGradient(len + Math.cos(a) * d, Math.sin(a) * d, 0, len + Math.cos(a) * d, Math.sin(a) * d, w * (0.4 + u));
    g.addColorStop(0, `rgba(220,248,255,${0.5 * fade * (1 - u)})`); g.addColorStop(1, 'rgba(220,248,255,0)');
    c.fillStyle = g; c.beginPath(); c.arc(len + Math.cos(a) * d, Math.sin(a) * d, w * (0.4 + u), 0, TAU); c.fill();
  }
  c.globalCompositeOperation = op;
}

/** 빙벽이 지나온 바닥 — 얇은 얼음판과 군데군데 남은 얼음 조각(멀수록 옅다) */
export function iceTrail(c: C2, sd: number, x0: number, x1: number, y: number, T: number) {
  const a = Math.min(x0, x1), b = Math.max(x0, x1); if (b - a < 4) return;
  const g = c.createLinearGradient(x0, 0, x1, 0); g.addColorStop(0, 'rgba(190,235,255,0)'); g.addColorStop(1, 'rgba(190,235,255,.8)');
  c.fillStyle = g; c.fillRect(a, y - 3, b - a, 4);
  c.fillStyle = 'rgba(255,255,255,.7)'; c.fillRect(a, y - 3, b - a, 1);
  const dir = x1 > x0 ? 1 : -1;
  for (let i = 0; i < 40; i++) {
    const d = 14 + i * 22 + hs(sd, i + 500) * 10; if (d > b - a) break;
    const xx = x1 - dir * d, k = 1 - d / Math.max(80, b - a);
    iceSpire(c, sd + i * 3.3, xx, y, 4 + hs(sd, i + 510) * 4, (5 + hs(sd, i + 520) * 12) * (0.4 + k * 0.6), (hs(sd, i + 530) - 0.5) * 5);
  }
}

/** 빙벽 앞날 밑 — 밀려 튀는 눈보라 */
export function snowSpray(c: C2, sd: number, x: number, y: number, dir: number, T: number) {
  for (let i = 0; i < 16; i++) {
    const u = (T * 2.6 + hs(sd, i + 600)) % 1, a = -Math.PI / 2 + dir * (0.4 + hs(sd, i + 610) * 0.9), d = u * (20 + hs(sd, i + 620) * 30);
    const px = x + Math.cos(a) * d, py = y - 2 + Math.sin(a) * d + u * u * 18, r = 2 + u * 5;
    const g = c.createRadialGradient(px, py, 0, px, py, r); g.addColorStop(0, `rgba(245,252,255,${0.85 * (1 - u)})`); g.addColorStop(1, 'rgba(245,252,255,0)');
    c.fillStyle = g; c.beginPath(); c.arc(px, py, r, 0, TAU); c.fill();
  }
}
